import { useState } from 'react';

const NAV_ITEMS = [
  {
    section: 'Dashboards',
    icon: '▦',
    items: ['Sales Overview', 'Revenue Trends', 'Product Performance'],
  },
  {
    section: 'Data',
    icon: '⊞',
    items: ['Upload Data', 'Data Sources', 'Data Explorer'],
  },
  {
    section: 'Workflows',
    icon: '⟳',
    items: ['Active Workflows', 'Scheduled Reports', 'Alerts'],
  },
];

function Placeholder({ label }) {
  return (
    <div style={s.placeholder}>
      <div style={s.placeholderIcon}>🚧</div>
      <h2 style={s.placeholderTitle}>{label}</h2>
      <p style={s.placeholderDesc}>This section is coming soon. Your content will appear here once your account is fully set up.</p>
    </div>
  );
}

export default function CustomerPortal({ onExit }) {
  const [activeSection, setActiveSection] = useState('Dashboards');
  const [activeItem, setActiveItem] = useState('Sales Overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div style={s.root}>

      {/* TOP BAR */}
      <header style={s.topBar}>
        <div style={s.topBarLeft}>
          <button style={s.menuToggle} onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle sidebar">
            &#9776;
          </button>
          <img src="/yhd_logo.png" alt="Yellowhammer Data" style={s.topLogo} />
          <span style={s.portalLabel}>Client Portal</span>
        </div>
        <div style={s.topBarRight}>
          <span style={s.welcomeText}>Welcome back</span>
          <button style={s.exitBtn} onClick={onExit}>&#8592; Back to Site</button>
        </div>
      </header>

      <div style={s.body}>

        {/* SIDEBAR */}
        {sidebarOpen && (
          <aside style={s.sidebar}>
            <nav style={s.sideNav}>
              {NAV_ITEMS.map(({ section, icon, items }) => (
                <div key={section} style={s.navGroup}>
                  <div style={s.navGroupLabel}>
                    <span style={s.navGroupIcon}>{icon}</span>
                    {section}
                  </div>
                  {items.map(item => {
                    const isActive = activeItem === item;
                    return (
                      <button
                        key={item}
                        style={{ ...s.navItem, ...(isActive ? s.navItemActive : {}) }}
                        onClick={() => { setActiveSection(section); setActiveItem(item); }}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>

            <div style={s.sidebarFooter}>
              <div style={s.sidebarFooterLabel}>Need help?</div>
              <a href="mailto:hello@yellowhammerdata.com" style={s.sidebarFooterLink}>
                hello@yellowhammerdata.com
              </a>
            </div>
          </aside>
        )}

        {/* MAIN CONTENT */}
        <main style={s.main}>
          <div style={s.mainInner}>
            <div style={s.breadcrumb}>
              <span style={s.breadcrumbSection}>{activeSection}</span>
              <span style={s.breadcrumbSep}>/</span>
              <span style={s.breadcrumbItem}>{activeItem}</span>
            </div>
            <Placeholder label={activeItem} />
          </div>
        </main>

      </div>
    </div>
  );
}

const s = {
  root: { display: 'flex', flexDirection: 'column', height: '100vh', background: '#F8FAFC', fontFamily: 'inherit' },

  // TOP BAR
  topBar: { height: '64px', background: '#1A2E44', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.18)', flexShrink: 0, zIndex: 10 },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  topBarRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  menuToggle: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '1.3rem', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 },
  topLogo: { height: '36px', width: 'auto', filter: 'brightness(0) invert(1)' },
  portalLabel: { color: '#F5A623', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase', borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: '16px' },
  welcomeText: { color: 'rgba(255,255,255,0.55)', fontSize: '0.88rem' },
  exitBtn: { background: 'transparent', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.25)', padding: '7px 16px', borderRadius: '7px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 },

  // BODY
  body: { display: 'flex', flex: 1, overflow: 'hidden' },

  // SIDEBAR
  sidebar: { width: '240px', background: 'white', borderRight: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' },
  sideNav: { flex: 1, padding: '24px 0 16px' },
  navGroup: { marginBottom: '8px' },
  navGroupLabel: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 20px 6px', fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em' },
  navGroupIcon: { fontSize: '0.9rem', color: '#F5A623' },
  navItem: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '9px 20px 9px 36px', fontSize: '0.9rem', color: '#4B5563', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, borderRadius: 0 },
  navItemActive: { background: '#F0F7FF', color: '#1A2E44', fontWeight: 700, borderRight: '3px solid #F5A623' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid #E5E7EB' },
  sidebarFooterLabel: { fontSize: '0.75rem', color: '#9CA3AF', marginBottom: '4px', fontWeight: 600 },
  sidebarFooterLink: { fontSize: '0.82rem', color: '#1A2E44', textDecoration: 'none', fontWeight: 500 },

  // MAIN
  main: { flex: 1, overflowY: 'auto', padding: '40px' },
  mainInner: { maxWidth: '900px' },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px' },
  breadcrumbSection: { fontSize: '0.85rem', color: '#9CA3AF', fontWeight: 500 },
  breadcrumbSep: { color: '#D1D5DB', fontSize: '0.85rem' },
  breadcrumbItem: { fontSize: '0.85rem', color: '#1A2E44', fontWeight: 600 },

  // PLACEHOLDER
  placeholder: { background: 'white', border: '1px dashed #D1D5DB', borderRadius: '16px', padding: '80px 40px', textAlign: 'center' },
  placeholderIcon: { fontSize: '2.5rem', marginBottom: '20px' },
  placeholderTitle: { fontSize: '1.4rem', fontWeight: 800, color: '#1A2E44', marginBottom: '12px' },
  placeholderDesc: { fontSize: '0.97rem', color: '#6B7280', maxWidth: '420px', margin: '0 auto', lineHeight: 1.7 },
};
